// The lists that sit in an object's own tab (Medlemskab, Adgang, Tilladelser, Regel ...). They are all built from the
// shared side-panel pieces (DetailPanel.jsx): a Fold per group, rows that look the same everywhere, settings in a
// framed block, and a quiet chevron instead of a "Vis flere" text.
import React, { useMemo, useState } from 'react';
import { MissingDash } from './Missing';
import SearchInput from './SearchInput';
import { Group, RowButton, MoreToggle, DataBlock, PanelLabel } from './DetailPanel';
import { permissionDescription } from '../data/permissionText';

const PAGE = 30;

// A list of people or things, PAGE at a time, with a chevron for the rest.
function PagedRows({ items, render }) {
  const [shown, setShown] = useState(PAGE);
  const visible = items.slice(0, shown);
  return (
    <>
      {visible.map(render)}
      {items.length > shown && <MoreToggle open={false} hiddenCount={items.length - shown} onClick={() => setShown((n) => n + PAGE)} />}
    </>
  );
}

// ---------------------------------------------------------------------------
// Medlemmer — groups, teams, roles and licences: Ejere, the members and Gæster, each in its own fold.
// ---------------------------------------------------------------------------
export function MembersPanel({ owners = [], members = [], hideOwners = false, membersLabel = 'Medlemmer' }) {
  const [query, setQuery] = useState('');
  const byName = (a, b) => (a.displayName || '').localeCompare(b.displayName || '', 'da');
  const groups = useMemo(() => ({
    o: hideOwners ? [] : [...owners].sort(byName),
    m: members.filter((m) => !m.isGuest).sort(byName),
    g: members.filter((m) => m.isGuest).sort(byName),
  }), [owners, members, hideOwners]);
  const total = groups.o.length + groups.m.length + groups.g.length;
  if (total === 0) return <p className="dp-muted">Ingen registreret.</p>;

  const q = query.trim().toLowerCase();
  const match = (m) => !q || (m.displayName || '').toLowerCase().includes(q) || (m.userPrincipalName || m.mail || '').toLowerCase().includes(q);
  const SECTIONS = [['o', 'Ejere', null], ['m', membersLabel, null], ['g', 'Gæster udefra', 'warn']];
  return (
    <>
      {total > 0 && <div style={{ marginBottom: 12 }}><SearchInput value={query} onChange={setQuery} placeholder="Søg på navn eller email…" /></div>}
      {SECTIONS.map(([kind, label, tone]) => {
        const list = groups[kind].filter(match);
        if (groups[kind].length === 0) return null;
        return (
          <Group key={`${kind}-${q}`} label={label} count={list.length} tone={tone}>
            {list.length === 0 ? <p className="dp-muted" style={{ padding: 8 }}>Ingen matcher “{query}”.</p> : (
              <PagedRows
                items={list}
                render={(m) => <RowButton key={m.id || m.userPrincipalName || m.displayName} title={m.displayName} sub={m.userPrincipalName || m.mail || undefined} pill={kind === 'g' ? { tone: 'warn', text: 'Gæst' } : undefined} />}
              />
            )}
          </Group>
        );
      })}
    </>
  );
}

// ---------------------------------------------------------------------------
// App-tilladelser — name + plain explanation, sorted by how powerful each is.
// The level is derived from the permission's NAME (…ReadWrite.All etc.) —
// Graph doesn't classify them for us, so this is a sort, not a verdict.
// ---------------------------------------------------------------------------
const FULL_RE = /FullControl|full_access_as_app|RoleManagement\.ReadWrite|Directory\.ReadWrite|AppRoleAssignment\.ReadWrite|Application\.ReadWrite|Policy\.ReadWrite|\.Manage\.All/i;
// Only the action part of the name ("Read", "ReadWrite", "ManageAsApp" ...) says what a permission can do. The resource part
// can contain the same letters ("DeviceManagementApps.Read.All" is a read permission although "Management" contains "Manage").
const WRITE_ACTION_RE = /^(ReadWrite|Write|Send|Manage|Delete|Update|Create|Full|Export|Invite|EnableDisable|Privileged)/i;

export function permissionLevel(p) {
  if (['Standardadgang', 'Ikke bekræftet', 'Ukendt'].includes(p.name)) return 'other';
  if (FULL_RE.test(p.name)) return 'full';
  const action = String(p.name || '').split('.')[1] || '';
  if (WRITE_ACTION_RE.test(action)) return 'write';
  return 'read';
}

const LEVEL = {
  full: { label: 'Fuld kontrol', tone: 'bad', rank: 0 },
  write: { label: 'Kan ændre', tone: 'warn', rank: 1 },
  read: { label: 'Kan læse', tone: 'neutral', rank: 2 },
  other: { label: 'Uklart niveau', tone: 'neutral', rank: 3 },
};

export function PermissionsPanel({ permissions = [], count }) {
  const enriched = useMemo(() => permissions.map((p) => ({ ...p, level: permissionLevel(p) })), [permissions]);
  const total = enriched.length;

  if (total === 0) {
    return <p className="dp-muted">{count > 0 ? 'Tilladelsernes navne kunne ikke hentes fra Microsoft lige nu — prøv igen senere.' : 'Appen har ingen tilladelser.'}</p>;
  }

  const n = (lv) => enriched.filter((p) => p.level === lv).length;
  const byResource = new Map();
  enriched.forEach((p) => {
    if (!byResource.has(p.resource)) byResource.set(p.resource, []);
    byResource.get(p.resource).push(p);
  });
  // Most serious group first — the resource holding the strongest permission leads, then the bigger group.
  const groups = [...byResource.entries()]
    .map(([resource, items]) => [resource, items.sort((a, b) => LEVEL[a.level].rank - LEVEL[b.level].rank || a.name.localeCompare(b.name))])
    .sort((x, y) => LEVEL[x[1][0].level].rank - LEVEL[y[1][0].level].rank || y[1].length - x[1].length);

  return (
    <>
      <p className="dp-tx">{total} tilladelser: {n('full')} fuld kontrol, {n('write')} kan ændre, {n('read')} kan læse{n('other') ? `, ${n('other')} uklart niveau` : ''}.</p>
      {groups.map(([resource, items]) => (
        <Group key={resource} label={resource} count={items.length} tone={LEVEL[items[0].level].tone === 'neutral' ? undefined : LEVEL[items[0].level].tone}>
          <PagedRows
            items={items}
            render={(p, idx) => (
              <RowButton
                key={`${p.name}-${idx}`}
                title={p.name}
                sub={p.level === 'other' ? p.description : permissionDescription(p.name)}
                pill={{ tone: LEVEL[p.level].tone, text: LEVEL[p.level].label }}
              />
            )}
          />
        </Group>
      ))}
    </>
  );
}

// ---------------------------------------------------------------------------
// Loginregel — the rule as one sentence, then who / what / where as settings, the requirements as settings,
// and the exceptions in a fold.
// ---------------------------------------------------------------------------
export function RulePanel({ item }) {
  const controls = item?.controlsShort || [];
  const has = (label) => controls.some((c) => c.toLowerCase() === label.toLowerCase());
  const blocked = has('Blokeret');
  const whoFull = item?.includeUsers || 'Alle brugere';
  // The short form ("89 roller, 6 grupper") for the sentence; the full name list is the longer value.
  const whoShort = (item?.meta || []).find(([k]) => k === 'Gælder for')?.[1] || whoFull;
  const allUsers = whoShort.toLowerCase() === 'alle brugere';
  const whoInSentence = allUsers ? 'en bruger' : /^\d/.test(whoShort) ? `en person i ${whoShort}` : whoShort;
  const apps = item?.includeApps || 'Alle apps';
  const where = item?.locationsInclude || 'Alle lokationer';
  const risk = [...(item?.riskLevels?.signIn || []), ...(item?.riskLevels?.user || [])];
  const session = item?.sessionControlsSummary || [];
  const exceptions = [
    ...(item?.excludeUsersList || []).map((name) => ({ name, sub: 'Undtaget bruger eller gruppe' })),
    ...(item?.excludeAppsList || []).map((name) => ({ name, sub: 'Undtaget app' })),
    ...(item?.locationsExclude ? item.locationsExclude.split(', ').map((name) => ({ name, sub: 'Undtaget lokation' })) : []),
  ];

  const requirement = blocked
    ? 'blive blokeret'
    : controls.length === 0
      ? (session.length > 0 ? 'følge sessionsreglerne nedenfor' : 'ikke opfylde nogen særlige krav')
      : has('MFA') && controls.length === 1
        ? 'bekræfte med MFA'
        : `opfylde: ${controls.join(item?.operator === 'OR' ? ' eller ' : ' og ')}`;

  // Everything else the rule can demand, shown as ordinary requirements.
  const otherControls = controls.filter((c) => !['mfa', 'compliant enhed', 'blokeret'].includes(c.toLowerCase()));
  const platforms = (item?.platformsInclude || []).filter((p) => p !== 'all');
  const clientTypes = (item?.clientAppTypes || []).filter((c) => c !== 'Alle klienter');
  const conditions = [
    platforms.length ? ['Platforme', platforms.join(', ')] : null,
    clientTypes.length ? ['Klienttyper', clientTypes.join(', ')] : null,
    item?.deviceFilter?.rule ? [`Enhedsfilter (${item.deviceFilter.mode === 'exclude' ? 'undtager' : 'gælder kun'})`, item.deviceFilter.rule] : null,
    (item?.authFlows || []).length ? ['Login-flow', item.authFlows.join(', ')] : null,
    (item?.userActions || []).length ? ['Handling', item.userActions.join(', ')] : null,
  ].filter(Boolean);

  const stateNote = item?.state === 'disabled'
    ? 'Reglen er slået fra og gør ikke noget lige nu.'
    : item?.state === 'enabledForReportingButNotEnforced'
      ? 'Reglen er kun i testtilstand: den logger, men håndhæver ikke.'
      : null;

  const requirements = blocked
    ? [['Adgangen', 'Blokeres']]
    : [
      ['Bekræft med MFA', has('MFA') ? 'Kræves' : 'Kræves ikke'],
      ['Enheden skal være compliant', has('Compliant enhed') ? 'Kræves' : 'Kræves ikke'],
      ['Ekstra krav ved risikabelt login', risk.length ? `Kræves (${risk.join(', ')})` : 'Kræves ikke'],
      ...otherControls.map((c) => [c, 'Kræves']),
      ...session.map((s) => [s, 'Kræves']),
    ];

  return (
    <>
      <p className="dp-tx">
        Når <b>{whoInSentence}</b> logger ind på <b>{apps === 'Alle apps' ? 'en hvilken som helst app' : apps}</b>, skal personen <b>{requirement}</b>.
      </p>
      {stateNote && <p className="dp-muted" style={{ marginTop: 8 }}>{stateNote}</p>}
      {item?.requiresAppProtection && (
        <p className="dp-muted" style={{ marginTop: 8 }}>Reglen kræver app-beskyttelse. Den virker kun, hvis der er en tildelt app-beskyttelsespolitik i Intune.</p>
      )}
      <PanelLabel>Hvem, hvad og hvorfra</PanelLabel>
      <DataBlock rows={[['Hvem reglen gælder for', whoFull !== whoShort ? `${whoShort} (${whoFull})` : whoShort], ['Hvad de logger ind på', apps], ['Hvorfra', where]]} />
      <PanelLabel>Krav for at komme ind</PanelLabel>
      <DataBlock rows={requirements} />
      {conditions.length > 0 && (
        <>
          <PanelLabel>Kun under disse betingelser</PanelLabel>
          <DataBlock rows={conditions} />
        </>
      )}
      {exceptions.length > 0 && <AccessList sections={[{ label: 'Undtagelser', items: exceptions }]} />}
    </>
  );
}

// ---------------------------------------------------------------------------
// A plain list of named things, one fold per heading. Used for a rule's exceptions and a mailbox's access.
// sections: [{ label, items: [{ name, sub }], missing?: bool }]
// ---------------------------------------------------------------------------
export function AccessList({ sections = [] }) {
  // A section with nothing in it is left out instead of saying "Ingen" —
  // only sections that have rows (or that we couldn't look up) take space.
  const shown = sections.filter((section) => section.missing || section.items.length > 0);
  if (shown.length === 0) return <p className="dp-muted">Ingen registreret.</p>;
  if (shown.every((section) => section.missing)) return <p className="dp-muted dp-gap">Ikke tilgængeligt endnu</p>;
  return (
    <>
      {shown.map((section) => (
        <Group key={section.label} label={section.label} count={section.missing ? '—' : section.items.length}>
          {section.missing
            ? <p className="dp-muted" style={{ padding: 8 }}><MissingDash /></p>
            : <PagedRows items={section.items} render={(it, idx) => <RowButton key={`${it.name}-${idx}`} title={it.name} sub={it.sub || undefined} />} />}
        </Group>
      ))}
    </>
  );
}

// ---------------------------------------------------------------------------
// Automatisk oprydning (Microsoft 365-gruppers "lifecycle"/udløbspolitik) in
// plain language.
// ---------------------------------------------------------------------------
export function describeCleanup(lp) {
  if (!lp) {
    return {
      status: 'Slået fra',
      tone: 'bad',
      title: 'Ubrugte grupper bliver liggende for evigt',
      text: 'Ingen bliver bedt om at bekræfte, at gruppen stadig bruges, og den slettes aldrig automatisk. Slås automatisk oprydning til, spørger Microsoft ejeren løbende — svarer ingen, slettes gruppen.',
    };
  }
  if (lp.appliesToAllGroups) {
    return {
      status: 'Slået til for alle grupper',
      tone: 'ok',
      title: 'Gruppen ryddes op automatisk',
      text: `Ejeren bliver spurgt hver ${lp.groupLifetimeInDays}. dag. Svarer ingen, slettes gruppen — og kan gendannes i 30 dage.`,
    };
  }
  if (lp.managedGroupTypes === 'None') {
    return {
      status: 'Slået fra',
      tone: 'bad',
      title: 'Oprydning er sat op, men gælder ingen grupper',
      text: 'Der er en politik for automatisk oprydning, men den er ikke slået til for nogen grupper. Ubrugte grupper bliver derfor liggende.',
    };
  }
  return {
    status: 'Slået til for nogle grupper',
    tone: '',
    title: 'Vi kan ikke se, om denne gruppe er med',
    text: `Oprydning (${lp.groupLifetimeInDays} dage) er kun slået til for udvalgte grupper. Tjek i Microsoft Entra, om denne gruppe er blandt dem.`,
  };
}

export function CleanupNote({ policy }) {
  const c = describeCleanup(policy);
  return (
    <>
      <PanelLabel>Automatisk oprydning</PanelLabel>
      <DataBlock rows={[['Status', c.status, false, c.tone === 'bad']]} />
      <p className="dp-tx" style={{ marginTop: 10 }}><b>{c.title}.</b> {c.text}</p>
    </>
  );
}
